import React from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { TotalResidentialSalesTable } from "../TotalResidentialSalesTable/TotalResidentialSalesTable";
import { MatchbackSalesTable } from "../MatchbackSalesTable/MatchbackSalesTable";
import { DirectMailSpendTable } from "../DirectMailSpendTable/DirectMailSpendTable";
import { MarketingROICard } from "../MarketingROICard/MarketingROICard";
import { ConversionRateCard } from "../ConversionRateCard/ConversionRateCard";
import { AverageSaleValueCard } from "../AverageSaleValueCard/AverageSaleValueCard";
import { AttributionRateCard } from "../AttributionRateCard/AttributionRateCard";
import { CostPerAcquisitionCard } from "../CostPerAcquisitionCard/CostPerAcquisitionCard";
import { MonthlySalesTrendChart } from "../MonthlySalesTrendChart/MonthlySalesTrendChart";
import { SpendVsRevenueChart } from "../SpendVsRevenueChart/SpendVsRevenueChart";
import ROIByMonthChart from "../ROIByMonthChart/ROIByMonthChart";
import { useROIDashboard } from "../../hooks/useROIDashboard";
import { LastSyncStatus } from "@/modules/stochastic/features/shared/components/LastSyncStatus/LastSyncStatus";

export const ROIDashboardPage: React.FC = () => {
  const {
    loading,
    refreshing,
    dashboardData,
    handleRefresh,
    matchbackReportDownloading,
    handleDownloadMatchbackReport,
  } = useROIDashboard();

  const periodLabel = new Date().toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const lastSyncDisplay = (
    <div className="flex items-center gap-3">
      <LastSyncStatus lastSync={dashboardData.lastSync} />
      <button
        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
        disabled={refreshing}
        onClick={handleRefresh}
      >
        <ArrowPathIcon
          className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
        />
        Refresh
      </button>
    </div>
  );

  return (
    <MainPageWrapper
      actions={lastSyncDisplay}
      loading={loading}
      subtitle="Marketing Efficiency Analysis"
      title="ROI Dashboard"
    >
      <div className="space-y-6">
        {dashboardData.roiCard && (
          <MarketingROICard
            roiPerDollar={dashboardData.roiCard.roiPerDollar}
            totalMarketingSpend={dashboardData.roiCard.totalMarketingSpend}
            totalMatchbackSales={dashboardData.roiCard.totalMatchbackSales}
          />
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <ConversionRateCard
            conversionRate={dashboardData.metrics?.conversionRate ?? null}
            periodLabel={periodLabel}
          />
          <AverageSaleValueCard
            averageSaleValue={dashboardData.metrics?.averageSaleValue ?? null}
            periodLabel={periodLabel}
          />
          <AttributionRateCard
            attributionRate={dashboardData.metrics?.attributionRate ?? null}
            periodLabel={periodLabel}
          />
          <CostPerAcquisitionCard
            costPerAcquisition={
              dashboardData.metrics?.costPerAcquisition ?? null
            }
            periodLabel={periodLabel}
          />
        </div>
        <MonthlySalesTrendChart
          data={dashboardData.charts?.monthlySalesTrend ?? null}
        />
        <SpendVsRevenueChart
          data={dashboardData.charts?.spendVsRevenue ?? null}
        />
        <ROIByMonthChart data={dashboardData.charts?.roiByMonth ?? null} />
        <TotalResidentialSalesTable
          data={dashboardData.tables?.residentialSales ?? null}
        />
        <MatchbackSalesTable
          data={dashboardData.tables?.matchbackSales ?? null}
          downloading={matchbackReportDownloading}
          onDownloadReport={handleDownloadMatchbackReport}
        />
        <DirectMailSpendTable
          data={dashboardData.tables?.directMailSpend ?? null}
        />
      </div>
    </MainPageWrapper>
  );
};
